import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import Collapse from "@material-ui/core/Collapse";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import MaterialTable from "material-table";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import Button from "components/CustomButtons/Button.js";
import SnackBarWiiba from "components/Snackbar/SnackBarWiiba";
import useMessage from "../../hooks/useMessage";
import useControlCaja from "../../hooks/useControlCaja";
import ModalIniciaCaja from "./ModalIniciaCaja";
import ModalPrevioCierre from "./ModalPrevioCierre";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import LockIcon from "@material-ui/icons/Lock";
import WarningIcon from "@material-ui/icons/Warning";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import SearchIcon from "@material-ui/icons/Search";
import Api from "../../functions/Api";
import { ValidationResponse } from "../../functions/validation";

const useStyles = makeStyles(theme => ({
  root: { width: "100%" },
  statusCard: {
    borderRadius: 12, padding: "20px 24px", marginBottom: 20,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexWrap: "wrap", gap: 16, border: "1px solid #e0e0e0",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  statusOpen: { backgroundColor: "#e8f5e9", borderColor: "#a5d6a7" },
  statusClosed: { backgroundColor: "#f5f5f5", borderColor: "#e0e0e0" },
  statusVencida: { backgroundColor: "#fff3e0", borderColor: "#ffcc80" },
  statusIcon: { fontSize: 40, marginRight: 16 },
  statusTitle: { fontSize: 16, fontWeight: 700 },
  statusSub: { fontSize: 12, color: "#666", marginTop: 2 },
  alertCard: {
    borderRadius: 12, padding: "16px 20px", marginBottom: 20,
    backgroundColor: "#fff3e0", border: "1px solid #ffcc80",
    display: "flex", alignItems: "center", gap: 12,
  },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, color: "#546e7a",
    textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12,
  },
  lastClose: {
    borderRadius: 8, padding: "14px 18px", backgroundColor: "#f8f9fa",
    border: "1px solid #e0e0e0", marginBottom: 16,
  },
  lcRow: {
    display: "flex", justifyContent: "space-between", padding: "3px 0",
  },
  lcLabel: { fontSize: 12, color: "#90a4ae" },
  lcValue: { fontSize: 13, fontWeight: 600, color: "#263238" },
  filterBar: {
    display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap",
    marginBottom: 16, padding: "12px 16px", backgroundColor: "#f8f9fa",
    borderRadius: 8, border: "1px solid #e0e0e0",
  },
}));

const fmtMoney = val =>
  "$" + parseFloat(val || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 });

const fmtDate = str => {
  if (!str) return "—";
  const d = new Date(str);
  return isNaN(d) ? str : d.toLocaleString("es-MX", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
};

const movColumns = [
  { title: "Fecha", field: "fecha", render: r => fmtDate(r.fecha) },
  { title: "Concepto", field: "concepto" },
  {
    title: "Tipo", field: "tipoDescripcion",
    render: r => (
      <Chip label={r.tipoDescripcion} size="small" style={{
        backgroundColor: r.tipo === "E" ? "#e8f5e9" : "#ffebee",
        color: r.tipo === "E" ? "#2e7d32" : "#c62828", fontSize: 10, fontWeight: 600
      }} />
    )
  },
  { title: "Monto", field: "monto", render: r => fmtMoney(r.monto) },
  { title: "Referencia", field: "referencia" },
  { title: "Método Pago", field: "metodoPago" },
];

const histColumns = [
  { title: "Cajero", field: "NOMBRE_CAJERO" },
  { title: "Apertura", field: "FECHA_CIERRE_INI", render: r => fmtDate(r.FECHA_CIERRE_INI) },
  { title: "Cierre", field: "FECHA_CIERRE_FIN", render: r => r.FECHA_CIERRE_FIN ? fmtDate(r.FECHA_CIERRE_FIN) : "—" },
  { title: "Inicial", field: "MONTO_INICIAL", render: r => fmtMoney(r.MONTO_INICIAL) },
  { title: "Entradas", field: "TOTAL_ENTRADAS", render: r => fmtMoney(r.TOTAL_ENTRADAS) },
  { title: "Salidas", field: "TOTAL_SALIDAS", render: r => fmtMoney(r.TOTAL_SALIDAS) },
  {
    title: "Esperado efectivo", field: "ESPERADO_EFECTIVO",
    render: r => r.ESPERADO_EFECTIVO != null ? fmtMoney(r.ESPERADO_EFECTIVO) : fmtMoney(r.MONTO_FINAL)
  },
  { title: "Declarado", field: "MONTO_DECLARADO", render: r => r.MONTO_DECLARADO != null ? fmtMoney(r.MONTO_DECLARADO) : "—" },
  {
    title: "Diferencia", field: "DIFERENCIA",
    render: r => {
      if (r.DIFERENCIA == null) return "—";
      const d = parseFloat(r.DIFERENCIA);
      const color = d === 0 ? "#2e7d32" : d > 0 ? "#f57f17" : "#c62828";
      return <span style={{ color, fontWeight: 700 }}>{d > 0 ? "+" : ""}{fmtMoney(d)}</span>;
    }
  },
  {
    title: "Estatus", field: "ESTATUS",
    render: r => (
      <Chip label={r.ESTATUS === "A" ? "Abierta" : "Cerrada"} size="small" style={{
        backgroundColor: r.ESTATUS === "A" ? "#e8f5e9" : "#f5f5f5",
        color: r.ESTATUS === "A" ? "#2e7d32" : "#546e7a", fontWeight: 600
      }} />
    )
  }
];

// Sub-component: movimientos de caja abierta
function MovimientosCajaSection({ userId, handleMuestraSnack }) {
  const [ejecutaapi] = Api();
  const [movimientos, setMovimientos] = useState([]);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const cargar = () => {
    ejecutaapi("ObtieneMovimientosCajaAbierta", JSON.stringify({ idCajero: userId })).then(data => {
      let { result, error } = ValidationResponse(data);
      if (error) { handleMuestraSnack(error, "error"); return; }
      if (result[0] && result[0].error === 1) { setMovimientos([]); return; }
      setMovimientos(Array.isArray(result[1]) ? result[1] : []);
      setLoaded(true);
    });
  };

  const handleToggle = () => {
    if (!loaded) cargar();
    setVisible(!visible);
  };

  return (
    <>
      <Divider style={{ margin: "20px 0 12px" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Typography style={{ fontSize: 11, fontWeight: 700, color: "#546e7a", textTransform: "uppercase", letterSpacing: 0.8 }}>
          Movimientos de la caja abierta
        </Typography>
        <Button size="sm" color={visible ? "danger" : "info"} onClick={handleToggle}
          style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {visible ? <><VisibilityOffIcon style={{ fontSize: 16 }} /> Ocultar</> : <><VisibilityIcon style={{ fontSize: 16 }} /> Ver movimientos</>}
        </Button>
      </div>
      <Collapse in={visible}>
        <MaterialTable
          title="" columns={movColumns} data={movimientos}
          options={{
            search: true, paging: true, pageSize: 10, toolbar: true,
            headerStyle: { fontWeight: 700, backgroundColor: "#f5f5f5", fontSize: 12 },
            rowStyle: r => ({ backgroundColor: r.tipo === "E" ? "#f9fbe7" : r.tipo === "S" ? "#fce4ec" : "#fff" })
          }}
          localization={{ body: { emptyDataSourceMessage: "Sin movimientos" } }}
        />
      </Collapse>
    </>
  );
}

export default function ControlCaja() {
  const classes = useStyles();
  const { opensnack, typesnackbar, message, handleMuestraSnack, handleClosesSnack } = useMessage();
  const {
    getInicioCaja, cierreCaja, getGeneraCierreCaja,
    cajasVencidas, getCajasVencidas, cajaActual, getEstadoCaja,
    historialCierres, getHistorialCierres, iniciaCajaRes, getConsultaCajaXDia
  } = useControlCaja({ handleMuestraSnack });

  const usuario = JSON.parse(localStorage.getItem("user"));
  const userId = usuario.userId;
  const esAdmin = usuario.rol === "admin";

  const [modalIniciaCaja, setModalIniciaCaja] = useState(false);
  const [modalCierreCaja, setModalCierreCaja] = useState(false);

  // Filtros historial admin
  const today = new Date().toISOString().split("T")[0];
  const [filtroFechaIni, setFiltroFechaIni] = useState(today);
  const [filtroFechaFin, setFiltroFechaFin] = useState(today);
  const [filtroCajero, setFiltroCajero] = useState(0);

  useEffect(() => {
    getEstadoCaja(userId);
    if (esAdmin) {
      getCajasVencidas();
      getHistorialCierres(today, today, 0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cajaAbierta = cajaActual && cajaActual.error === 0;
  const cajaVencida = cajaActual && cajaActual.vencida === 1;

  // Último cierre del cajero (de iniciaCajaRes)
  const ultimoCierre = iniciaCajaRes && iniciaCajaRes.length > 0
    ? iniciaCajaRes.find(c => c.ESTATUS === "C") : null;

  const handleAbrirCierre = () => {
    setModalCierreCaja(true);
    getGeneraCierreCaja(userId, 0, false, null);
  };

  const handleBuscarHistorial = () => {
    getHistorialCierres(filtroFechaIni, filtroFechaFin, filtroCajero);
  };

  return (
    <div className={classes.root}>
      <SnackBarWiiba opensnack={opensnack} tipoSnackBar={typesnackbar}
        mensaje={message} handleClosesSnack={handleClosesSnack} />

      <Card>
        <CardHeader color="primary">
          <h4 style={{ margin: 0, fontWeight: 600 }}>Control de Caja</h4>
        </CardHeader>
        <CardBody>

          {/* ALERTA CAJAS VENCIDAS (solo admin) */}
          {esAdmin && cajasVencidas.length > 0 && (
            <div className={classes.alertCard}>
              <WarningIcon style={{ color: "#e65100", fontSize: 28 }} />
              <div>
                <Typography style={{ fontWeight: 700, color: "#e65100" }}>
                  {cajasVencidas.length} caja{cajasVencidas.length > 1 ? "s" : ""} sin cerrar
                </Typography>
                {cajasVencidas.map(c => (
                  <Typography key={c.ID_CIERRE_CAJA} variant="caption" style={{ display: "block", color: "#666" }}>
                    {c.NOMBRE_CAJERO} — abierta hace {c.HORAS_ABIERTA}h (desde {fmtDate(c.FECHA_CIERRE_INI)})
                  </Typography>
                ))}
              </div>
            </div>
          )}

          {/* ESTADO DE CAJA ACTUAL */}
          <div className={`${classes.statusCard} ${cajaVencida ? classes.statusVencida : cajaAbierta ? classes.statusOpen : classes.statusClosed}`}>
            <div style={{ display: "flex", alignItems: "center" }}>
              {cajaAbierta && !cajaVencida
                ? <LockOpenIcon className={classes.statusIcon} style={{ color: "#2e7d32" }} />
                : cajaVencida
                  ? <WarningIcon className={classes.statusIcon} style={{ color: "#e65100" }} />
                  : <LockIcon className={classes.statusIcon} style={{ color: "#546e7a" }} />
              }
              <div>
                <Typography className={classes.statusTitle}>
                  {cajaVencida ? "Caja vencida — requiere cierre"
                    : cajaAbierta ? "Caja abierta"
                    : "Sin caja abierta"}
                </Typography>
                <Typography className={classes.statusSub}>
                  {cajaActual?.fechaApertura
                    ? `Apertura: ${fmtDate(cajaActual.fechaApertura)}`
                    : "Abra una caja para registrar pagos"}
                </Typography>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {!cajaAbierta && !cajaVencida && (
                <Button color="success" size="sm" onClick={() => setModalIniciaCaja(true)}>
                  Abrir Caja
                </Button>
              )}
              {(cajaAbierta || cajaVencida) && (
                <Button color="warning" size="sm" onClick={handleAbrirCierre}>
                  Cerrar Caja
                </Button>
              )}
            </div>
          </div>

          {/* MOVIMIENTOS DE CAJA ABIERTA (cajero y admin) */}
          {(cajaAbierta || cajaVencida) && (
            <MovimientosCajaSection userId={userId} handleMuestraSnack={handleMuestraSnack} />
          )}

          {/* HISTORIAL DE CIERRES (solo admin) */}
          {esAdmin && (
            <>
              <Divider style={{ margin: "24px 0 16px" }} />
              <Typography className={classes.sectionTitle}>Historial de cierres</Typography>

              <div className={classes.filterBar}>
                <TextField
                  label="Desde" type="date" size="small" variant="outlined"
                  value={filtroFechaIni}
                  onChange={e => setFiltroFechaIni(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  style={{ minWidth: 150 }}
                />
                <TextField
                  label="Hasta" type="date" size="small" variant="outlined"
                  value={filtroFechaFin}
                  onChange={e => setFiltroFechaFin(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  style={{ minWidth: 150 }}
                />
                <TextField
                  label="Cajero" select size="small" variant="outlined"
                  value={filtroCajero}
                  onChange={e => setFiltroCajero(e.target.value)}
                  style={{ minWidth: 150 }}
                >
                  <MenuItem value={0}>Todos</MenuItem>
                  {cajasVencidas.map(c => (
                    <MenuItem key={c.ID_CAJERO} value={c.ID_CAJERO}>{c.NOMBRE_CAJERO}</MenuItem>
                  ))}
                </TextField>
                <Button size="sm" color="info" onClick={handleBuscarHistorial}
                  style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <SearchIcon style={{ fontSize: 16 }} /> Buscar
                </Button>
              </div>

              <MaterialTable
                title="" columns={histColumns} data={historialCierres}
                options={{
                  search: false, paging: true, pageSize: 10, toolbar: false,
                  headerStyle: { fontWeight: 700, backgroundColor: "#f5f5f5", fontSize: 11 },
                }}
                localization={{ body: { emptyDataSourceMessage: "Sin cierres en el rango seleccionado" } }}
              />
            </>
          )}

        </CardBody>
      </Card>

      <ModalIniciaCaja
        modalIniciaCaja={modalIniciaCaja} setModalIniciaCaja={setModalIniciaCaja}
        getInicioCaja={getInicioCaja} usuario={usuario}
      />
      <ModalPrevioCierre
        modalCierreCaja={modalCierreCaja} setModalCierreCaja={setModalCierreCaja}
        cierreCaja={cierreCaja} getGeneraCierreCaja={getGeneraCierreCaja} usuario={usuario}
      />
    </div>
  );
}
